import { AssessmentType, SurveyStatus } from '@@types/api.types';
import { useAssessmentsQueryOptions } from '@queries/assessment.query';
import { useGetSurveys } from '@queries/survey.query';
import { useGetTechnologyUsesWithAssessments } from '@queries/technology-uses.query';
import { useGetTechnology } from '@queries/technology.query';
import { useGetTechnologyTrlAssessment } from '@queries/technology.query';
import { useQuery } from '@tanstack/react-query';
import { FC, useCallback, useMemo } from 'react';
import { AccordionTile } from '../../shared/accordion-tile.component';
import { TechnologyAssessmentHistory } from '../../technology-assessment-history.component';
import { TRL1_ASSESSMENT_ID } from '../sidebar/trl-assessment-history-list.component';
import { DomainPerformanceRadarTile } from './domain-performance-radar-tile.component';
import { RecommendedActionsTile } from './recommended-actions-tile.component';
import { TrlSummaryStats } from './trl-summary-stats.component';
import { UsesPerformanceBarTile } from './uses-performance-bar-tile.component';

type TrlOverviewContentProps = {
  onNavigateToAssessment: (assessmentId: string | null) => void;
  technologyId: string;
};

export const TrlOverviewContent: FC<TrlOverviewContentProps> = (props) => {
  const { onNavigateToAssessment, technologyId } = props;

  const { data: technology } = useGetTechnology(technologyId);

  // Handle timeline chart clicks - convert null (TRL1 synthetic entry) to TRL1_ASSESSMENT_ID
  const handleTimelineClick = useCallback(
    (assessmentId: string | null) => {
      onNavigateToAssessment(assessmentId ?? TRL1_ASSESSMENT_ID);
    },
    [onNavigateToAssessment],
  );

  const assessmentQueryOptions = useAssessmentsQueryOptions({
    technologyId,
    type: [AssessmentType.TRL],
  });
  const { data: assessments = [], isPending: isAssessmentsLoading } = useQuery(assessmentQueryOptions);

  const { data: draftSurveys = [] } = useGetSurveys({
    status: SurveyStatus.DRAFT,
    technologyId,
  });

  const { data: featuredAssessment } = useGetTechnologyTrlAssessment(technologyId);
  const { data: technologyUses = [] } = useGetTechnologyUsesWithAssessments(technologyId);

  const hasDraftSurvey = draftSurveys.some((s) => s.assessmentType === AssessmentType.TRL);
  const hasFeaturedAssessment = Boolean(featuredAssessment);

  // Find use+domain combinations without assessments
  const usesWithoutAssessments = useMemo(() => {
    const missing: Array<{ domainName: string; useName: string }> = [];

    for (const use of technologyUses) {
      for (const domain of use.domains) {
        if (!domain.featuredAssessment) {
          missing.push({ domainName: domain.name, useName: use.name });
        }
      }
    }

    return missing;
  }, [technologyUses]);

  const trlDescription =
    'Technology Readiness Level (TRL) is a measurement system used to assess the maturity of a particular technology. The scale ranges from 1 to 9, where TRL 1 represents basic principles observed and TRL 9 represents actual system proven through successful mission operations.';

  return (
    <div className="flex flex-col gap-4">
      {/* Technology Readiness Description */}
      <AccordionTile
        content={trlDescription}
        title="Technology Readiness Level"
        value="trl-description"
      />

      {/* Row 1: Assessment History (Full Width) */}
      <TechnologyAssessmentHistory
        fixedType={AssessmentType.TRL}
        onDataPointClick={handleTimelineClick}
      />

      {/* Row 2: Uses TRL Performance Across Domains (Full Width) */}
      <UsesPerformanceBarTile technologyId={technologyId} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DomainPerformanceRadarTile technologyId={technologyId} />

        {/* Row 4: TRL Summary Stats + Recommended Actions */}
        <div className="flex flex-col gap-4">
          <TrlSummaryStats
            assessments={assessments}
            isLoading={isAssessmentsLoading}
            onNavigateToAssessment={onNavigateToAssessment}
          />
          <RecommendedActionsTile
            hasDraftSurvey={hasDraftSurvey}
            hasFeaturedAssessment={hasFeaturedAssessment}
            usesWithoutAssessments={usesWithoutAssessments}
          />
        </div>
      </div>
    </div>
  );
};
